import { format, formatDistanceToNow } from "date-fns";

/**
 * Formats an ISO timestamp into a human-friendly relative time string.
 *
 * @param isoTimestamp - ISO 8601 timestamp string
 * @returns Formatted string:
 *   - <1 minute: "Just now"
 *   - <60 minutes: "X minutes ago"
 *   - <24 hours: "X hours ago"
 *   - ≥24 hours: "MMM d, yyyy 'at' h:mm a" (e.g., "Jan 15, 2025 at 2:30 PM")
 *
 * @example
 * formatRelativeTime("2025-01-15T14:30:00Z") // "2 minutes ago"
 * formatRelativeTime("2025-01-14T10:00:00Z") // "Jan 14, 2025 at 10:00 AM"
 */
export function formatRelativeTime(isoTimestamp: string): string {
  try {
    const date = new Date(isoTimestamp);

    // Validate date
    if (isNaN(date.getTime())) {
      return "Unknown";
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    // Less than 1 minute
    if (diffInMinutes < 1) {
      return "Just now";
    }

    // Less than 24 hours - use relative format
    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // 24 hours or more - use absolute format
    return format(date, "MMM d, yyyy 'at' h:mm a");
  } catch (error) {
    console.error("Error formatting relative time:", error);
    return "Unknown";
  }
}
